import { ReservationSortSchema, type ReservationSort } from '@atomfit/shared';
import { reservationStatus, type ReservationStatus } from '@atomfit/shared';
import { useQueryFilter } from '../../hooks/useQueryFilter.js';
import { translate } from '../../i18n/index.js';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  Download,
  Plus,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import '../../styles/atomfit/reservation-filters.css';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AccessCodeStatus, Gym, PricingTier } from '@atomfit/shared';
import { InfiniteScroll } from '../../components/common/InfiniteScroll.js';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { CustomSelect, Spinner, Tag, formatCurrency } from '../../components/design/AtomFitUI.js';
import {
  bookingsApi,
  type ManagedBookingDetails,
  type OwnerBooking,
} from '../../api/bookings.api.js';
import { gymsApi } from '../../api/gyms.api.js';
import { groupAdminBookings, type AdminBookingRow } from '../../utils/admin-booking-groups.js';
import { useAuth } from '../../hooks/useAuth.js';

const STATUSES: ReservationStatus[] = [
  'PENDING',
  'PAID',
  'ACTIVE',
  'COMPLETED',
  'NO_SHOW',
  'CANCELLED',
  'REFUNDED',
];

const RANGE_OPTIONS = [
  { value: 'today', label: translate('pages.admin.admin_bookings_page.range_options.label') },
  { value: 'week', label: translate('pages.admin.admin_bookings_page.range_options.time') },
  {
    value: 'lastWeek',
    label: translate('pages.admin.admin_bookings_page.range_options.time_2'),
  },
  { value: 'month', label: translate('pages.admin.admin_bookings_page.range_options.time_3') },
  { value: 'custom', label: translate('pages.admin.admin_bookings_page.range_options.label_2') },
] as const;

type RangeMode = '' | 'today' | 'week' | 'lastWeek' | 'month' | 'custom';
type RefundMode = 'CREDIT' | 'CASH' | 'NONE';
type BookingFilterParams = NonNullable<Parameters<typeof bookingsApi.adminList>[0]>;

const statusColor: Record<ReservationStatus, { color: string; bg: string; label: string }> = {
  PENDING: {
    color: 'var(--af-muted)',
    bg: 'rgba(255,255,255,0.06)',
    label: translate('pages.admin.admin_bookings_page.status_color.pending'),
  },
  PAID: {
    color: 'var(--af-success)',
    bg: 'rgba(74,222,128,0.1)',
    label: translate('pages.admin.admin_bookings_page.status_color.label'),
  },
  ACTIVE: {
    color: 'var(--af-accent)',
    bg: 'var(--af-accent-soft)',
    label: translate('pages.admin.admin_bookings_page.status_color.active'),
  },
  NO_SHOW: {
    color: 'var(--af-danger)',
    bg: 'rgba(232,66,26,0.1)',
    label: translate('reservation.status.noShow'),
  },
  COMPLETED: {
    color: 'var(--af-dim)',
    bg: 'rgba(255,255,255,0.04)',
    label: translate('pages.admin.admin_bookings_page.status_color.completed'),
  },
  CANCELLED: {
    color: 'var(--af-danger)',
    bg: 'rgba(232,66,26,0.1)',
    label: translate('pages.admin.admin_bookings_page.status_color.cancelled'),
  },
  REFUNDED: {
    color: 'var(--af-danger)',
    bg: 'rgba(232,66,26,0.1)',
    label: translate('pages.admin.admin_bookings_page.status_color.refund'),
  },
};

const accessCodeStatusMeta: Record<AccessCodeStatus, { color: string; bg: string; label: string }> =
  {
    ACTIVE: {
      color: 'var(--af-success)',
      bg: 'rgba(74,222,128,0.1)',
      label: translate('pages.admin.admin_bookings_page.access_code_status_meta.active'),
    },
    USED: {
      color: 'var(--af-accent)',
      bg: 'var(--af-accent-soft)',
      label: translate('pages.admin.admin_bookings_page.access_code_status_meta.label'),
    },
    EXPIRED: {
      color: 'var(--af-dim)',
      bg: 'rgba(255,255,255,0.04)',
      label: translate('pages.admin.admin_bookings_page.access_code_status_meta.label_2'),
    },
    REVOKED: {
      color: 'var(--af-danger)',
      bg: 'rgba(232,66,26,0.1)',
      label: translate('pages.admin.admin_bookings_page.access_code_status_meta.label_3'),
    },
  };

const tierLabel: Record<PricingTier, string> = {
  STANDARD: translate('pages.admin.admin_bookings_page.tier_label.standard'),
  OFF_PEAK: translate('pages.admin.admin_bookings_page.tier_label.off_peak'),
  PEAK: translate('pages.admin.admin_bookings_page.tier_label.peak'),
  LATE_NIGHT: translate('pages.admin.admin_bookings_page.tier_label.late_night'),
};

function formatDateTime(value: string | Date) {
  return new Date(value).toLocaleString('cs-CZ', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Prague',
  });
}

function toInputDateTime(date = new Date()) {
  const copy = new Date(date);
  copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset());
  return copy.toISOString().slice(0, 16);
}

function paymentLabel(booking: OwnerBooking) {
  if (booking.payment) return booking.payment.status;
  if (['PAID', 'ACTIVE', 'COMPLETED'].includes(booking.status)) return 'MANUAL_PAID';
  return 'UNPAID';
}

function fullName(booking: OwnerBooking) {
  return [booking.account.firstName, booking.account.lastName].filter(Boolean).join(' ');
}

function accessUsageText(accessCode: NonNullable<OwnerBooking['accessCode']>) {
  const parts: string[] = [];
  if (accessCode.pinUsedAt) {
    parts.push(
      translate('pages.admin.admin_bookings_page.access_code_detail.pin_used', {
        time: formatDateTime(accessCode.pinUsedAt),
      }),
    );
  }
  if (accessCode.remoteOpenedAt) {
    parts.push(
      translate('pages.admin.admin_bookings_page.access_code_detail.remote_opened', {
        time: formatDateTime(accessCode.remoteOpenedAt),
      }),
    );
  }
  if (accessCode.attemptCount > 0) {
    parts.push(
      translate('pages.admin.admin_bookings_page.access_code_detail.attempt_text', {
        count: accessCode.attemptCount,
      }),
    );
  }
  return parts.length > 0
    ? parts.join(' · ')
    : translate('pages.admin.admin_bookings_page.access_code_detail.attempt_text_2');
}

function accessCodeDetail(accessCode: OwnerBooking['accessCode']) {
  if (!accessCode) return translate('pages.admin.admin_bookings_page.access_code_detail.copy');
  const meta = accessCodeStatusMeta[accessCode.status];
  const codeText = accessCode.code
    ? translate('pages.admin.admin_bookings_page.access_code_detail.code_text', {
        code: accessCode.code,
      })
    : translate('pages.admin.admin_bookings_page.access_code_detail.code_text_2');
  return translate('pages.admin.admin_bookings_page.access_code_detail.status_attempts_code', {
    code: codeText,
    status: meta.label,
    from: formatDateTime(accessCode.validFrom),
    until: formatDateTime(accessCode.validUntil),
    attempts: accessUsageText(accessCode),
  });
}

export function AdminBookingsPage() {
  const { role } = useAuth();
  const [, setUrlFilters] = useSearchParams();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const isOperator = role === 'GYM_STAFF';
  const [bookings, setBookings] = useState<OwnerBooking[]>([]);
  const bookingRows = useMemo(() => groupAdminBookings(bookings), [bookings]);
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const [sortParam] = useQueryFilter<string>('sortBy', 'scheduled');
  const [directionParam] = useQueryFilter<string>('sortDirection', 'asc');
  const sortBy = ReservationSortSchema.safeParse(sortParam).data ?? 'scheduled';
  const sortDirection = directionParam === 'desc' ? 'desc' : 'asc';
  function changeSort(column: ReservationSort) {
    setUrlFilters(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('sortBy', column);
        next.set('sortDirection', sortBy === column && sortDirection === 'asc' ? 'desc' : 'asc');
        return next;
      },
      { replace: true },
    );
  }
  const [statusFilter, setStatusFilter] = useQueryFilter<ReservationStatus | ''>('status');
  const [gymFilter, setGymFilter] = useQueryFilter('gymId');
  const [scheduledRange, setScheduledRange] = useQueryFilter<string>('range', 'today');
  const range = scheduledRange === 'all' ? '' : (scheduledRange as RangeMode);
  const setRange = (value: RangeMode) => setScheduledRange(value || 'all');
  const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Prague' });
  const [from, setFrom] = useQueryFilter('from', today);
  const [to, setTo] = useQueryFilter('to', today);
  const [createdRange, setCreatedRange] = useQueryFilter('createdRange');
  const [createdFrom, setCreatedFrom] = useQueryFilter('createdFrom', today);
  const [createdTo, setCreatedTo] = useQueryFilter('createdTo', today);
  const [creator, setCreator] = useQueryFilter('creator');
  const [metric, setMetric] = useQueryFilter('metric');
  const [metricFrom, setMetricFrom] = useQueryFilter('metricFrom');
  const [metricTo, setMetricTo] = useQueryFilter('metricTo');
  const [tenantSlug] = useQueryFilter('tenantSlug');
  const [createdByAccountId] = useQueryFilter('createdByAccountId');
  const [search] = useQueryFilter('q');
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  const [accountIdFilter] = useQueryFilter('accountId');
  const advancedFilterCount = [createdRange, creator, metric].filter(Boolean).length;
  const [advancedOpen, setAdvancedOpen] = useState(advancedFilterCount > 0);
  useEffect(() => {
    if (advancedFilterCount > 0) setAdvancedOpen(true);
  }, [advancedFilterCount]);
  const hasScope = Boolean(tenantSlug || createdByAccountId || accountIdFilter);
  const hasFilters = Boolean(
    range || gymFilter || statusFilter || search || advancedFilterCount || hasScope,
  );

  function clearFilters() {
    setSearchInput('');
    setUrlFilters(
      (current) => {
        const next = new URLSearchParams(current);
        for (const key of [
          'q',
          'gymId',
          'status',
          'from',
          'to',
          'createdRange',
          'createdFrom',
          'createdTo',
          'creator',
          'metric',
          'metricFrom',
          'metricTo',
          'tenantSlug',
          'createdByAccountId',
          'accountId',
        ])
          next.delete(key);
        next.set('range', 'all');
        return next;
      },
      { replace: true },
    );
  }
  const [total, setTotal] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [details, setDetails] = useState<(ManagedBookingDetails & AdminBookingRow) | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<AdminBookingRow | null>(null);
  const [refundMode, setRefundMode] = useState<RefundMode>('NONE');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [manageTarget, setManageTarget] = useState<AdminBookingRow | null>(null);
  const [manageAction, setManageAction] = useState<'status' | 'delete'>('status');
  const [nextStatus, setNextStatus] = useState<ReservationStatus>('PENDING');
  const [saving, setSaving] = useState(false);
  const [manageError, setManageError] = useState<string | null>(null);

  async function confirmManage() {
    if (!manageTarget || !role || saving) return;
    setSaving(true);
    setManageError(null);
    try {
      if (manageAction === 'delete') await bookingsApi.adminDelete(role, manageTarget.id);
      else await bookingsApi.adminUpdateStatus(role, manageTarget.id, nextStatus);
      setManageTarget(null);
      setDetails(null);
    } catch (error) {
      const response = (error as { response?: { data?: { error?: { message?: string } } } })
        .response;
      setManageError(response?.data?.error?.message ?? translate('reservation.manage.error'));
    } finally {
      await loadPage(1, 'replace');
      setSaving(false);
    }
  }

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [manual, setManual] = useState({
    gymId: '',
    slotStartTime: toInputDateTime(new Date(Date.now() + 60 * 60 * 1000)),
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    price: '',
    paymentStatus: 'PAID' as 'PENDING' | 'PAID',
    notes: '',
  });

  const filterParams = useMemo(() => {
    const params: Omit<BookingFilterParams, 'page'> = { sortBy, sortDirection };

    if (statusFilter) params.status = statusFilter;
    if (gymFilter) params.gymId = gymFilter;
    if (accountIdFilter) params.accountId = accountIdFilter;
    if (search) params.q = search;
    if (range) params.range = range;
    if (range === 'custom' && from) params.from = from;
    if (range === 'custom' && to) params.to = to;
    if (createdRange) params.createdRange = createdRange;
    if (createdRange === 'custom') {
      params.createdFrom = createdFrom;
      params.createdTo = createdTo;
    }
    if (creator) params.creator = creator;
    if (createdByAccountId) params.createdByAccountId = createdByAccountId;
    if (tenantSlug) params.tenantSlug = tenantSlug;
    if (metric) {
      params.metric = metric;
      params.metricFrom = metricFrom;
      params.metricTo = metricTo;
    }
    return params;
  }, [
    sortBy,
    sortDirection,
    accountIdFilter,
    from,
    gymFilter,
    range,
    search,
    statusFilter,
    to,
    createdRange,
    createdFrom,
    createdTo,
    creator,
    createdByAccountId,
    tenantSlug,
    metric,
    metricFrom,
    metricTo,
  ]);

  const loadPage = useCallback(
    async (nextPage = 1, mode: 'replace' | 'append' = 'replace') => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoadError(null);
      if (mode === 'replace') {
        setLoading(true);
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }
      try {
        const res = isPlatform
          ? await bookingsApi.adminList({ ...filterParams, page: nextPage })
          : await bookingsApi.ownerList({ ...filterParams, page: nextPage });
        if (requestId !== requestIdRef.current) return;
        setBookings((current) => {
          if (mode === 'replace') return res.data.data;
          const existing = new Set(current.map((booking) => booking.id));
          return [...current, ...res.data.data.filter((booking) => !existing.has(booking.id))];
        });
        setTotal(res.data.meta.total);
        setTotalCustomers(res.data.meta.totalCustomers ?? 0);
        setPage(res.data.meta.page);
        setTotalPages(res.data.meta.totalPages);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setLoadError(
          translate(
            'pages.admin.admin_bookings_page.admin_bookings_page.set_load_error.error_booking',
          ),
        );
        if (mode === 'replace') {
          setBookings([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          if (mode === 'replace') {
            setLoading(false);
          } else {
            setLoadingMore(false);
          }
        }
      }
    },
    [filterParams, isPlatform],
  );

  useEffect(() => {
    void loadPage(1, 'replace');
  }, [loadPage]);

  useEffect(() => {
    void gymsApi.adminList(role ?? '', 1).then((res) => {
      setGyms(res.data.data);
      if (!manual.gymId && res.data.data[0]) {
        setManual((current) => ({ ...current, gymId: res.data.data[0]?.id ?? '' }));
      }
    });
  }, [manual.gymId, role]);

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setUrlFilters(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('accountId');
        if (searchInput.trim()) next.set('q', searchInput.trim());
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
  }

  async function openDetails(booking: AdminBookingRow) {
    if (!role) return;
    setDetailsLoading(true);
    try {
      const responses = await Promise.all(
        booking.groupedBookings.map((item) => bookingsApi.adminGet(role, item.id)),
      );
      const items = responses.map((response) => response.data);
      setDetails({
        ...items[0]!,
        ...groupAdminBookings(items)[0]!,
        events: items
          .flatMap((item) => item.events)
          .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt)),
        notificationDeliveries: items.flatMap((item) => item.notificationDeliveries),
      });
    } finally {
      setDetailsLoading(false);
    }
  }

  async function confirmCancel() {
    if (!cancelTarget || !role) return;
    setCancelling(true);
    try {
      for (const item of cancelTarget.groupedBookings) {
        if (!['PENDING', 'PAID', 'ACTIVE'].includes(item.status)) continue;
        await bookingsApi.adminCancel(role, item.id, {
          refundMode,
          ...(cancelReason.trim() ? { reason: cancelReason.trim() } : {}),
        });
      }
      setCancelReason('');
      setRefundMode('NONE');
    } finally {
      setCancelTarget(null);
      void loadPage(1, 'replace');
      setCancelling(false);
    }
  }

  async function createManualBooking(e: React.FormEvent) {
    e.preventDefault();
    if (!role) return;
    setCreating(true);
    try {
      await bookingsApi.adminCreateManual(role, {
        gymId: manual.gymId,
        slotStartTime: new Date(manual.slotStartTime).toISOString(),
        email: manual.email,
        ...(manual.firstName ? { firstName: manual.firstName } : {}),
        ...(manual.lastName ? { lastName: manual.lastName } : {}),
        ...(manual.phone ? { phone: manual.phone } : {}),
        ...(manual.price ? { priceInSmallestUnit: Math.round(Number(manual.price) * 100) } : {}),
        paymentStatus: manual.paymentStatus,
        ...(manual.notes ? { notes: manual.notes } : {}),
      });
      setCreateOpen(false);
      setManual((current) => ({
        ...current,
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        price: '',
        notes: '',
      }));
      void loadPage(1, 'replace');
    } finally {
      setCreating(false);
    }
  }

  async function exportCsv() {
    if (!role) return;
    const res = await bookingsApi.adminExportCsv(role, filterParams);
    const url = URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bookings.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  const eyebrow =
    role === 'PLATFORM_ADMIN'
      ? translate('pages.admin.admin_bookings_page.admin_bookings_page.eyebrow')
      : isOperator
        ? translate('pages.admin.admin_bookings_page.admin_bookings_page.eyebrow_2')
        : translate('pages.admin.admin_bookings_page.admin_bookings_page.eyebrow_3');
  const hasMoreBookings = page < totalPages;

  return (
    <AdminLayout
      title={translate('pages.admin.admin_bookings_page.admin_bookings_page.admin_layout.booking')}
      eyebrow={eyebrow}
    >
      <div className="af-card af-reservation-filters">
        <div className="af-reservation-toolbar">
          <div className="af-reservation-heading">
            <SlidersHorizontal size={18} aria-hidden="true" />
            <h2>{translate('reservation.filters.title')}</h2>
          </div>
          {!isOperator && (
            <div className="af-reservation-actions">
              <button className="af-outline-btn" type="button" onClick={() => void exportCsv()}>
                <Download size={16} aria-hidden="true" />
                {translate('reservation.filters.export')}
              </button>
              <button className="af-primary-btn" type="button" onClick={() => setCreateOpen(true)}>
                <Plus size={17} aria-hidden="true" />
                {translate('reservation.filters.new')}
              </button>
            </div>
          )}
        </div>
        <form
          onSubmit={applySearch}
          role="search"
          aria-label={translate('reservation.filters.title')}
        >
          <div className="af-reservation-filter-grid">
            <div className="af-label af-reservation-search">
              <label htmlFor="reservation-search">
                {translate(
                  'pages.admin.admin_bookings_page.admin_bookings_page.label_label.search',
                )}
              </label>
              <span className="af-reservation-search-control">
                <Search size={16} aria-hidden="true" />
                <input
                  className="af-input"
                  id="reservation-search"
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={translate(
                    'pages.admin.admin_bookings_page.admin_bookings_page.input_input.placeholder_email_phone_name',
                  )}
                />
                <button
                  type="submit"
                  className="af-reservation-search-submit"
                  aria-label={translate(
                    'pages.admin.admin_bookings_page.admin_bookings_page.label_label.search',
                  )}
                >
                  <Search size={17} aria-hidden="true" />
                </button>
              </span>
            </div>
            <label className="af-label">
              {translate('report.scheduled')}

              <CustomSelect
                value={range}
                onChange={(value) => {
                  setRange(value as RangeMode);
                }}
                options={[
                  {
                    value: '',
                    label: translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.custom_select.options',
                    ),
                  },
                  ...RANGE_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
                ]}
              />
            </label>
            <label className="af-label">
              {translate('pages.admin.admin_bookings_page.admin_bookings_page.label_label.gym')}

              <CustomSelect
                value={gymFilter}
                onChange={(value) => {
                  setGymFilter(value);
                }}
                options={[
                  {
                    value: '',
                    label: translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.custom_select.options_2',
                    ),
                  },
                  ...gyms.map((g) => ({ value: g.id, label: g.name })),
                ]}
              />
            </label>
            <label className="af-label">
              {translate('pages.admin.admin_bookings_page.admin_bookings_page.label_label.status')}

              <CustomSelect
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value as ReservationStatus | '');
                }}
                options={[
                  {
                    value: '',
                    label: translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.custom_select.options_3',
                    ),
                  },
                  ...STATUSES.map((s) => ({
                    value: s,
                    label: statusColor[s].label,
                  })),
                ]}
              />
            </label>
          </div>
          {range === 'custom' && (
            <div className="af-reservation-dates af-reservation-scheduled-dates">
              <label className="af-label">
                {translate(
                  'pages.admin.admin_bookings_page.admin_bookings_page.label_label.copy_2',
                )}

                <input
                  className="af-input"
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                  }}
                />
              </label>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_bookings_page.admin_bookings_page.label_label.copy_3',
                )}

                <input
                  className="af-input"
                  type="date"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value);
                  }}
                />
              </label>
            </div>
          )}{' '}
        </form>
        <div className="af-reservation-filter-options">
          <button
            className="af-reservation-text-button"
            type="button"
            aria-expanded={advancedOpen}
            aria-controls="reservation-advanced-filters"
            onClick={() => setAdvancedOpen((open) => !open)}
          >
            <ChevronDown size={16} aria-hidden="true" className={advancedOpen ? 'is-open' : ''} />
            {translate('reservation.filters.advanced')}
            {advancedFilterCount > 0 && (
              <span className="af-reservation-filter-count">{advancedFilterCount}</span>
            )}
          </button>
          {hasFilters && (
            <button className="af-reservation-text-button" type="button" onClick={clearFilters}>
              <X size={14} aria-hidden="true" />
              {translate('reservation.filters.clear')}
            </button>
          )}
        </div>
        <div id="reservation-advanced-filters" hidden={!advancedOpen}>
          <div className="af-reservation-advanced-grid">
            <div className="af-reservation-filter-group">
              <label className="af-label">
                {translate('report.createdFilter')}
                <CustomSelect
                  value={createdRange}
                  onChange={setCreatedRange}
                  options={[{ value: '', label: translate('report.all') }, ...RANGE_OPTIONS]}
                />
              </label>
              {createdRange === 'custom' && (
                <div className="af-reservation-dates">
                  <label className="af-label">
                    {translate('report.from')}
                    <input
                      className="af-input"
                      type="date"
                      value={createdFrom}
                      onChange={(e) => setCreatedFrom(e.target.value)}
                    />
                  </label>
                  <label className="af-label">
                    {translate('report.to')}
                    <input
                      className="af-input"
                      type="date"
                      value={createdTo}
                      onChange={(e) => setCreatedTo(e.target.value)}
                    />
                  </label>
                </div>
              )}
            </div>
            <label className="af-label">
              {translate('report.creator')}
              <input
                className="af-input"
                value={creator}
                onChange={(e) => setCreator(e.target.value)}
                placeholder={translate('report.creatorPlaceholder')}
              />
            </label>
            <div className="af-reservation-filter-group">
              <label className="af-label">
                {translate('report.outcome')}
                <CustomSelect
                  value={metric}
                  onChange={setMetric}
                  options={[
                    { value: '', label: translate('report.all') },
                    ...['created', 'cancelled', 'attended', 'noShow'].map((value) => ({
                      value,
                      label: translate(`report.${value}`),
                    })),
                  ]}
                />
              </label>
              {metric && (
                <div className="af-reservation-dates">
                  <label className="af-label">
                    {translate('report.from')}
                    <input
                      className="af-input"
                      type="date"
                      value={metricFrom}
                      onChange={(e) => setMetricFrom(e.target.value)}
                    />
                  </label>
                  <label className="af-label">
                    {translate('report.to')}
                    <input
                      className="af-input"
                      type="date"
                      value={metricTo}
                      onChange={(e) => setMetricTo(e.target.value)}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="af-reservation-filter-footer">
          <p role="status" aria-live="polite">
            {loading
              ? translate('report.loading')
              : loadError
                ? translate('reservation.filters.countError')
                : translate('report.bookingCounts', { total, customers: totalCustomers })}
          </p>
          {hasScope && (
            <span className="af-reservation-scope">{translate('reservation.filters.scope')}</span>
          )}
          {(tenantSlug || createdByAccountId || accountIdFilter) && (
            <button
              className="af-reservation-text-button"
              type="button"
              onClick={() =>
                setUrlFilters(
                  (current) => {
                    const next = new URLSearchParams(current);
                    for (const key of ['tenantSlug', 'createdByAccountId', 'accountId'])
                      next.delete(key);
                    return next;
                  },
                  { replace: true },
                )
              }
            >
              <X size={14} aria-hidden="true" />
              {translate('report.clearScope')}
            </button>
          )}
        </div>
      </div>

      <div className="af-card" style={{ overflow: 'auto' }}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: 24,
              color: 'var(--af-muted)',
            }}
          >
            <Spinner />{' '}
            {translate('pages.admin.admin_bookings_page.admin_bookings_page.div.loading_booking')}
          </div>
        ) : loadError && bookings.length === 0 ? (
          <div style={{ padding: 24 }}>
            <div className="af-error" style={{ marginBottom: 14 }}>
              {loadError}
            </div>
            <button
              className="af-outline-btn"
              type="button"
              onClick={() => void loadPage(1, 'replace')}
              style={{ padding: '10px 14px' }}
            >
              {translate(
                'pages.admin.admin_bookings_page.admin_bookings_page.button_outline_btn.retry',
              )}
            </button>
          </div>
        ) : bookings.length === 0 ? (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--af-muted)' }}>
            {translate('pages.admin.admin_bookings_page.admin_bookings_page.div.booking')}
          </div>
        ) : (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 1260 }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid var(--af-border)' }}>
                {(
                  [
                    {
                      key: 'tenant',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.tr.tenant',
                      ),
                    },
                    {
                      key: 'customer',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.tr.user',
                      ),
                    },
                    { key: 'createdAt', label: translate('report.createdFilter') },
                    { key: 'creator', label: translate('report.creator') },
                    {
                      key: 'phone',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.tr.phone',
                      ),
                    },
                    {
                      key: 'gym',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.tr.gym',
                      ),
                    },
                    {
                      key: 'scheduled',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.tr.copy',
                      ),
                    },
                    {
                      key: 'type',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.tr.type',
                      ),
                    },
                    {
                      key: 'price',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.tr.price',
                      ),
                    },
                    {
                      key: 'payment',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.tr.payment',
                      ),
                    },
                    {
                      key: 'code',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.tr.code',
                      ),
                    },
                    {
                      key: 'status',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.tr.status',
                      ),
                    },
                  ] satisfies { key: ReservationSort; label: string }[]
                )
                  .filter(
                    ({ key }) =>
                      (key !== 'tenant' || isPlatform) && (key !== 'price' || !isOperator),
                  )
                  .map(({ key, label }) => (
                    <th
                      key={key}
                      scope="col"
                      className="af-reservation-sort-header"
                      aria-sort={
                        sortBy === key
                          ? sortDirection === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <button
                        type="button"
                        onClick={() => changeSort(key)}
                        title={translate(
                          sortBy === key && sortDirection === 'asc'
                            ? 'reservation.sort.desc'
                            : 'reservation.sort.asc',
                          { column: label },
                        )}
                      >
                        {label}
                        {sortBy === key ? (
                          sortDirection === 'asc' ? (
                            <ArrowUp size={13} aria-hidden="true" />
                          ) : (
                            <ArrowDown size={13} aria-hidden="true" />
                          )
                        ) : (
                          <ArrowUpDown size={13} aria-hidden="true" />
                        )}
                      </button>
                    </th>
                  ))}
                <th scope="col" aria-label={translate('reservation.sort.actions')} />
              </tr>
            </thead>
            <tbody>
              {bookingRows.map((b, idx) => {
                const status = statusColor[reservationStatus(b.groupedBookings)];
                const cancellable = ['PENDING', 'PAID', 'ACTIVE'].includes(b.status);
                return (
                  <tr
                    key={b.id}
                    style={{
                      borderBottom:
                        idx < bookingRows.length - 1 ? '1px solid var(--af-border)' : 'none',
                    }}
                  >
                    {isPlatform && (
                      <td style={{ padding: '14px' }}>
                        <div style={{ fontWeight: 800 }}>{b.gym.tenant?.name ?? '-'}</div>
                        <div
                          className="af-mono"
                          style={{ fontSize: 11, color: 'var(--af-dim)', marginTop: 2 }}
                        >
                          {b.gym.tenant?.slug ?? ''}
                        </div>
                      </td>
                    )}
                    <td style={{ padding: '14px' }}>
                      <button
                        type="button"
                        onClick={() => void openDetails(b)}
                        style={{
                          display: 'block',
                          padding: 0,
                          border: 0,
                          background: 'transparent',
                          color: 'var(--af-text)',
                          fontWeight: 800,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        {fullName(b) || b.account.email}
                      </button>
                      <div
                        className="af-mono"
                        style={{ fontSize: 11, color: 'var(--af-dim)', marginTop: 2 }}
                      >
                        {b.account.email}
                      </div>
                    </td>
                    <td style={{ padding: '14px' }}>{formatDateTime(b.createdAt)}</td>
                    <td style={{ padding: '14px' }}>
                      {b.createdBy?.email ?? translate('report.unknown')}
                    </td>
                    <td style={{ padding: '14px', color: 'var(--af-muted)' }}>
                      {b.account.phone ?? '-'}
                    </td>
                    <td style={{ padding: '14px', color: 'var(--af-muted)' }}>{b.gym.name}</td>
                    <td style={{ padding: '14px', color: 'var(--af-muted)' }}>
                      {formatDateTime(b.slot.startTime)} – {formatDateTime(b.slot.endTime)}
                    </td>
                    <td style={{ padding: '14px', color: 'var(--af-muted)' }}>
                      {[
                        ...new Set(
                          b.groupedBookings.map((item) => tierLabel[item.slot.pricingTier]),
                        ),
                      ].join(' / ')}
                    </td>
                    {!isOperator && (
                      <td style={{ padding: '14px', color: 'var(--af-muted)' }}>
                        {formatCurrency(b.priceInSmallestUnit, b.currency)}
                      </td>
                    )}
                    <td style={{ padding: '14px' }}>
                      <Tag color="var(--af-muted)" bg="rgba(255,255,255,0.06)">
                        {[...new Set(b.groupedBookings.map(paymentLabel))].join(' / ')}
                      </Tag>
                    </td>
                    <td style={{ padding: '14px' }}>
                      <AccessCodeSummary accessCode={b.accessCode} />
                    </td>
                    <td style={{ padding: '14px' }}>
                      <Tag color={status.color} bg={status.bg}>
                        {status.label}
                      </Tag>
                    </td>
                    <td style={{ padding: '14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        className="af-outline-btn"
                        style={{ padding: '6px 10px', marginRight: 8, fontSize: 12 }}
                        type="button"
                        onClick={() => void openDetails(b)}
                      >
                        {translate(
                          'pages.admin.admin_bookings_page.admin_bookings_page.button_outline_btn.details',
                        )}
                      </button>
                      {!isOperator &&
                        (['status', 'delete'] as const).map((action) => (
                          <button
                            key={action}
                            type="button"
                            className="af-outline-btn"
                            style={{
                              padding: '6px 10px',
                              marginRight: 8,
                              fontSize: 12,
                              ...(action === 'delete' ? { color: 'var(--af-danger)' } : {}),
                            }}
                            onClick={() => {
                              setManageTarget(b);
                              setManageAction(action);
                              setNextStatus(reservationStatus(b.groupedBookings));
                              setManageError(null);
                            }}
                          >
                            {translate(`reservation.manage.${action}`)}
                          </button>
                        ))}
                      {cancellable && !isOperator && (
                        <button
                          onClick={() => setCancelTarget(b)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            border: '1px solid rgba(232,66,26,0.3)',
                            background: 'transparent',
                            color: 'var(--af-danger)',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {translate(
                            'pages.admin.admin_bookings_page.admin_bookings_page.button.cancel',
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && loadError && bookings.length > 0 && (
        <div className="af-error" style={{ marginTop: 12 }}>
          {loadError}
        </div>
      )}

      {!loading && bookings.length > 0 && (
        <InfiniteScroll
          hasMore={hasMoreBookings}
          isLoading={loadingMore}
          onLoadMore={() => void loadPage(page + 1, 'append')}
          loadingLabel={translate(
            'pages.admin.admin_bookings_page.admin_bookings_page.infinite_scroll.loading_label',
          )}
          endLabel={translate(
            'pages.admin.admin_bookings_page.admin_bookings_page.infinite_scroll.end_label',
          )}
          style={{ marginTop: 8 }}
        />
      )}

      {createOpen && !isOperator && (
        <div
          onClick={() => setCreateOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: 24,
          }}
        >
          <form
            className="af-card"
            style={{ width: '100%', maxWidth: 680, padding: 24 }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void createManualBooking(e)}
          >
            <h2 style={{ margin: '0 0 18px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.admin.admin_bookings_page.admin_bookings_page.h2.booking')}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label className="af-label">
                {translate('pages.admin.admin_bookings_page.admin_bookings_page.label_label.gym_2')}

                <CustomSelect
                  value={manual.gymId}
                  onChange={(gymId) => setManual({ ...manual, gymId })}
                  placeholder={translate(
                    'pages.admin.admin_bookings_page.admin_bookings_page.custom_select.placeholder_gym_select',
                  )}
                  options={gyms.map((g) => ({ value: g.id, label: g.name }))}
                />
              </label>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_bookings_page.admin_bookings_page.label_label.copy_4',
                )}

                <input
                  required
                  className="af-input"
                  type="datetime-local"
                  value={manual.slotStartTime}
                  onChange={(e) => setManual({ ...manual, slotStartTime: e.target.value })}
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_bookings_page.admin_bookings_page.label_label.email')}

                <input
                  required
                  className="af-input"
                  value={manual.email}
                  onChange={(e) => setManual({ ...manual, email: e.target.value })}
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_bookings_page.admin_bookings_page.label_label.phone')}

                <input
                  className="af-input"
                  value={manual.phone}
                  onChange={(e) => setManual({ ...manual, phone: e.target.value })}
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_bookings_page.admin_bookings_page.label_label.name')}

                <input
                  className="af-input"
                  value={manual.firstName}
                  onChange={(e) => setManual({ ...manual, firstName: e.target.value })}
                />
              </label>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_bookings_page.admin_bookings_page.label_label.last_name',
                )}

                <input
                  className="af-input"
                  value={manual.lastName}
                  onChange={(e) => setManual({ ...manual, lastName: e.target.value })}
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_bookings_page.admin_bookings_page.label_label.price')}

                <input
                  className="af-input"
                  inputMode="decimal"
                  value={manual.price}
                  onChange={(e) => setManual({ ...manual, price: e.target.value })}
                  placeholder={translate(
                    'pages.admin.admin_bookings_page.admin_bookings_page.input_input.placeholder_content',
                  )}
                />
              </label>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_bookings_page.admin_bookings_page.label_label.payment',
                )}

                <CustomSelect
                  value={manual.paymentStatus}
                  onChange={(paymentStatus) =>
                    setManual({
                      ...manual,
                      paymentStatus: paymentStatus as 'PENDING' | 'PAID',
                    })
                  }
                  options={[
                    {
                      value: 'PAID',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.custom_select.options_4',
                      ),
                    },
                    {
                      value: 'PENDING',
                      label: translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.custom_select.options_5',
                      ),
                    },
                  ]}
                />
              </label>
            </div>
            <label className="af-label" style={{ marginTop: 12 }}>
              {translate('pages.admin.admin_bookings_page.admin_bookings_page.label_label.copy_5')}

              <textarea
                className="af-input"
                value={manual.notes}
                onChange={(e) => setManual({ ...manual, notes: e.target.value })}
                rows={3}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button
                className="af-outline-btn"
                type="button"
                style={{ padding: '10px 18px' }}
                onClick={() => setCreateOpen(false)}
                disabled={creating}
              >
                {translate(
                  'pages.admin.admin_bookings_page.admin_bookings_page.button_outline_btn.back',
                )}
              </button>
              <button
                className="af-primary-btn"
                type="submit"
                style={{ padding: '10px 18px' }}
                disabled={creating}
              >
                {creating ? (
                  <Spinner />
                ) : (
                  translate(
                    'pages.admin.admin_bookings_page.admin_bookings_page.button_primary_btn.create_2',
                  )
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {(details || detailsLoading) && (
        <div
          onClick={() => setDetails(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: 24,
          }}
        >
          <div
            className="af-card"
            style={{
              width: '100%',
              maxWidth: 720,
              maxHeight: '86vh',
              overflow: 'auto',
              padding: 24,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {detailsLoading || !details ? (
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  color: 'var(--af-muted)',
                }}
              >
                <Spinner />{' '}
                {translate('pages.admin.admin_bookings_page.admin_bookings_page.div.loading')}
              </div>
            ) : (
              <>
                <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800 }}>
                  {fullName(details) || details.account.email}
                </h2>
                <div
                  className="af-mono"
                  style={{ color: 'var(--af-dim)', fontSize: 12, marginBottom: 16 }}
                >
                  {details.id}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 10,
                    marginBottom: 18,
                  }}
                >
                  <Info
                    label={translate('reservation.status.label')}
                    value={statusColor[reservationStatus(details.groupedBookings)].label}
                  />
                  <Info
                    label={translate('report.createdFilter')}
                    value={formatDateTime(details.createdAt)}
                  />
                  <Info
                    label={translate('report.creator')}
                    value={details.createdBy?.email ?? translate('report.unknown')}
                  />
                  <Info
                    label={translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.info.email',
                    )}
                    value={details.account.email}
                  />

                  <Info
                    label={translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.info.phone',
                    )}
                    value={details.account.phone ?? '-'}
                  />

                  <Info
                    label={translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.info.label',
                    )}
                    value={`${formatDateTime(details.slot.startTime)} – ${formatDateTime(details.slot.endTime)}`}
                  />

                  <Info
                    label={translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.info.type',
                    )}
                    value={[
                      ...new Set(
                        details.groupedBookings.map((item) => tierLabel[item.slot.pricingTier]),
                      ),
                    ].join(' / ')}
                  />

                  <Info
                    label={translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.info.code',
                    )}
                    value={accessCodeDetail(details.accessCode)}
                  />

                  {!isOperator && (
                    <Info
                      label={translate(
                        'pages.admin.admin_bookings_page.admin_bookings_page.info.price',
                      )}
                      value={formatCurrency(details.priceInSmallestUnit, details.currency)}
                    />
                  )}
                  <Info
                    label={translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.info.payment',
                    )}
                    value={[...new Set(details.groupedBookings.map(paymentLabel))].join(' / ')}
                  />
                </div>
                <h3
                  style={{
                    margin: '0 0 10px',
                    fontSize: 13,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 0,
                    color: 'var(--af-muted)',
                  }}
                >
                  {translate('pages.admin.admin_bookings_page.admin_bookings_page.h3.history')}
                </h3>
                <div style={{ display: 'grid', gap: 10 }}>
                  {details.events.map((event, idx) => (
                    <div
                      key={`${event.type}-${idx}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '160px 1fr',
                        gap: 12,
                        padding: '10px 0',
                        borderTop: idx === 0 ? 'none' : '1px solid var(--af-border)',
                      }}
                    >
                      <div style={{ color: 'var(--af-dim)', fontSize: 12 }}>
                        {formatDateTime(event.occurredAt)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 800 }}>{event.label}</div>
                        {event.detail && (
                          <div style={{ color: 'var(--af-muted)', fontSize: 12, marginTop: 2 }}>
                            {event.detail}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {manageTarget && (
        <div
          onClick={() => {
            if (!saving) setManageTarget(null);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: 24,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="manage-reservation-title"
            className="af-card"
            style={{ width: '100%', maxWidth: 480, padding: 28 }}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="manage-reservation-title">{translate(`reservation.manage.${manageAction}`)}</h2>
            <p>{manageTarget.account.email}</p>
            <p>
              {formatDateTime(manageTarget.slot.startTime)} –{' '}
              {formatDateTime(manageTarget.slot.endTime)}
            </p>
            <p style={{ color: 'var(--af-muted)' }}>
              {translate(`reservation.manage.${manageAction}Help`, {
                count: manageTarget.groupedBookings.length,
              })}
            </p>
            {manageAction === 'status' && (
              <label className="af-label">
                {translate('reservation.manage.status')}
                <CustomSelect
                  value={nextStatus}
                  disabled={saving}
                  onChange={(value) => setNextStatus(value as ReservationStatus)}
                  options={STATUSES.map((status) => ({
                    value: status,
                    label: statusColor[status].label,
                  }))}
                />
              </label>
            )}
            {manageError && (
              <p className="af-error" role="alert">
                {manageError}
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button
                className="af-outline-btn"
                style={{ padding: '10px 18px' }}
                disabled={saving}
                onClick={() => setManageTarget(null)}
              >
                {translate('reservation.manage.back')}
              </button>
              <button
                className="af-primary-btn"
                style={{ padding: '10px 18px' }}
                disabled={saving}
                onClick={() => void confirmManage()}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate(
                    `reservation.manage.${manageAction === 'delete' ? 'deleteConfirm' : 'save'}`,
                  )
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelTarget && (
        <div
          onClick={() => setCancelTarget(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: 24,
          }}
        >
          <div
            className="af-card"
            style={{ width: '100%', maxWidth: 460, padding: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.admin.admin_bookings_page.admin_bookings_page.h2.booking_cancel')}
            </h2>
            <p
              style={{
                margin: '0 0 16px',
                color: 'var(--af-muted)',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {translate('pages.admin.admin_bookings_page.admin_bookings_page.p.booking')}{' '}
              <strong style={{ color: 'var(--af-text)' }}>{cancelTarget.account.email}</strong>
              {translate(
                'pages.admin.admin_bookings_page.admin_bookings_page.p.slot_cancel_active',
              )}
            </p>
            <p style={{ color: 'var(--af-muted)', fontSize: 14 }}>
              {formatDateTime(cancelTarget.slot.startTime)} –{' '}
              {formatDateTime(cancelTarget.slot.endTime)}
              {cancelTarget.groupedBookings.length > 1 && (
                <span>
                  {' '}
                  ·{' '}
                  {translate('pages.customer.my_bookings.my_bookings.p.group_cancel', {
                    count: cancelTarget.groupedBookings.filter((item) =>
                      ['PENDING', 'PAID', 'ACTIVE'].includes(item.status),
                    ).length,
                  })}
                </span>
              )}
            </p>
            <label className="af-label">
              {translate('pages.admin.admin_bookings_page.admin_bookings_page.label_label.refund')}

              <CustomSelect
                value={refundMode}
                onChange={(value) => setRefundMode(value as RefundMode)}
                options={[
                  {
                    value: 'CREDIT',
                    label: translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.custom_select.options_6',
                    ),
                  },
                  {
                    value: 'CASH',
                    label: translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.custom_select.options_7',
                    ),
                  },
                  {
                    value: 'NONE',
                    label: translate(
                      'pages.admin.admin_bookings_page.admin_bookings_page.custom_select.options_8',
                    ),
                  },
                ]}
              />
            </label>
            <label className="af-label" style={{ marginTop: 12 }}>
              {translate('pages.admin.admin_bookings_page.admin_bookings_page.label_label.reason')}

              <textarea
                className="af-input"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </label>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button
                className="af-outline-btn"
                style={{ padding: '10px 20px' }}
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
              >
                {translate(
                  'pages.admin.admin_bookings_page.admin_bookings_page.button_outline_btn.back_2',
                )}
              </button>
              <button
                onClick={() => void confirmCancel()}
                disabled={cancelling}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '1px solid rgba(232,66,26,0.4)',
                  background: 'rgba(232,66,26,0.12)',
                  color: 'var(--af-danger)',
                  fontSize: 14,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                {cancelling ? (
                  <Spinner />
                ) : (
                  translate('pages.admin.admin_bookings_page.admin_bookings_page.button.cancel_2')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function AccessCodeSummary({ accessCode }: { accessCode: OwnerBooking['accessCode'] }) {
  if (!accessCode) {
    return (
      <div style={{ color: 'var(--af-dim)', fontSize: 12, fontWeight: 700 }}>
        {translate('pages.admin.admin_bookings_page.access_code_summary.div.copy')}
      </div>
    );
  }

  const meta = accessCodeStatusMeta[accessCode.status];

  return (
    <div style={{ display: 'grid', gap: 5, minWidth: 170 }}>
      <div className="af-mono" style={{ color: 'var(--af-text)', fontSize: 15, fontWeight: 900 }}>
        {accessCode.code ?? '-'}
      </div>
      <Tag color={meta.color} bg={meta.bg}>
        {meta.label}
      </Tag>
      <div style={{ color: 'var(--af-muted)', fontSize: 12, lineHeight: 1.35 }}>
        {formatDateTime(accessCode.validFrom)}
        <div style={{ color: 'var(--af-dim)' }}>
          {translate('pages.admin.admin_bookings_page.access_code_summary.div.copy_2')}{' '}
          {formatDateTime(accessCode.validUntil)}
        </div>
      </div>
      <div style={{ color: 'var(--af-dim)', fontSize: 11 }}>{accessUsageText(accessCode)}</div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ border: '1px solid var(--af-border)', borderRadius: 8, padding: 10 }}>
      <div style={{ color: 'var(--af-dim)', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ fontWeight: 800, fontSize: 13, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}
